import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardContent, Typography, RadioGroup, FormControlLabel, Radio, Container, Grid, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

const Test = () => {
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [timer, setTimer] = useState(45 * 60); // 45 minutes in seconds
  const [isTestGenerated, setIsTestGenerated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine); // Track online status
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const questionsPerPage = 10;
  const navigate = useNavigate();
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  useEffect(() => {
    // Disable right-click and copying
    const handleContextMenu = (e) => e.preventDefault();
    const handleCopy = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (e.ctrlKey && ["c", "u", "s"].includes(e.key)) e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleSubmit = useCallback(async () => {
    setShowConfirmDialog(false);
    const studentName = sessionStorage.getItem("SName");
    const studentEmail = sessionStorage.getItem("SEmail");
    const answers = Object.entries(userAnswers).map(([questionId, selectedOption]) => ({
      id: questionId,
      selectedOption,
    }));

    const payload = { studentName, studentEmail, answers };

    if (navigator.onLine) {
      try {
        setIsSubmitting(true);
        const response = await fetch(`/api/test/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Failed to submit the test");

        await response.json();
        alert("Test submitted successfully!");
        setQuestions([]);
        setUserAnswers({});
        navigate("/Test");
      } catch (error) {
        console.error("Error submitting test:", error);
        alert("Failed to submit the test. It will retry when online.");
        saveToLocalStorage("pendingSubmission", payload);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      alert("You are offline. Your test will be submitted automatically once you are online.");
      saveToLocalStorage("pendingSubmission", payload);
    }
  }, [userAnswers, navigate]);

  useEffect(() => {
    let interval;
    if (isTestGenerated && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      clearInterval(interval);
      handleSubmit(); // Auto-submit when the timer hits 0
    }
    return () => clearInterval(interval);
  }, [isTestGenerated, timer, handleSubmit]);

  const retryPendingSubmission = useCallback(async () => {
    const pendingSubmission = getFromLocalStorage("pendingSubmission");
    if (pendingSubmission) {
      try {
        const response = await fetch(`/api/test/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pendingSubmission),
        });
        if (response.ok) {
          alert("Pending test submitted successfully!");
          localStorage.removeItem("pendingSubmission");
          navigate("/Test");
        } else {
          throw new Error("Failed to submit pending test.");
        }
      } catch (error) {
        console.error("Error retrying submission:", error);
      }
    }
  }, [navigate]);

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
      if (navigator.onLine) retryPendingSubmission();
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, [retryPendingSubmission]);

  const saveToLocalStorage = (key, data) => localStorage.setItem(key, JSON.stringify(data));
  const getFromLocalStorage = (key) => JSON.parse(localStorage.getItem(key) || "null");

  const generateTest = async () => {
    try {
      const response = await fetch(`/api/test/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestType: "generateTest" }),
      });

      if (!response.ok) throw new Error("Failed to fetch questions");

      const data = await response.json();
      setQuestions(data.questions);
      setIsTestGenerated(true);
      setTimer(45 * 60); // Test timer for demonstration (10 seconds)
    } catch (error) {
      console.error("Error generating test:", error);
      alert("Failed to generate the test. Please try again.");
    }
  };

  const handleOptionSelect = (questionId, option) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: option,
    }));

  };

  const handleOptionChange = (id) => (event) => {
    handleOptionSelect(id, event.target.value);
  };

  const handleSubmitConfirmation = () => {
    setShowConfirmDialog(true); // Show the confirmation dialog
  };


  const handleCancelSubmit = () => {
    setShowConfirmDialog(false); // Close the dialog without submitting
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const currentQuestions = questions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <div
      className="p-4 background-radial-gradient overflow-hidden"
      style={{
        height: "fit-content",
        display: "flex",
        flexDirection: "column",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
    >
      <Container maxWidth="md">
        <Box className="TopHere">
          <div>
            <img src="/bntLogo.png" alt="Logo" className="logo2" />
          </div>
          <div className="testPageStartsHere">
            <Typography variant="h3" component="h1" color="white" gutterBottom>
              Online Test
            </Typography>
            {!isTestGenerated && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={generateTest}
                style={{ marginBottom: "73.1vh" }}
              >
                Generate Test
              </Button>
            )}
          </div>
        </Box>

        {isTestGenerated && (
          <>
            <Box textAlign="center" mb={3} sx={{
              position: "sticky",
              top: 0,
              zIndex: 1000,
              backgroundColor: "#d2dbe8",
              padding: "10px",
              borderBottom: "1px solid #ddd",
            }}>
              <Typography variant="h6" color={isOnline ? "green" : "red"}>
                {isOnline ? "Online" : "Offline"}
              </Typography>
              <Typography variant="h6" color="red">
                Time Remaining: {formatTime(timer)}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {currentQuestions.map((question) => (
                <Grid item xs={12} key={question.id}>
                  <Card sx={{ backgroundColor: "#d2dbe8", height: "fit-content" }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {`${(currentPage - 1) * questionsPerPage + currentQuestions.indexOf(question) + 1}. ${question.text}`}
                      </Typography>
                      <RadioGroup
                        value={userAnswers[question.id] || ""}
                        onChange={handleOptionChange(question.id)}
                      >
                        {question.options.map((option, index) => (
                          <FormControlLabel key={index} value={option} control={<Radio />} label={option} />
                        ))}
                      </RadioGroup>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box textAlign="center" mt={4} display="flex" justifyContent="space-between" alignItems="center">
              <Button
                variant="contained"
                disabled={currentPage === 1}
                onClick={handlePreviousPage}
              >
                Previous
              </Button>
              <Typography>
                Page {currentPage} of {totalPages}
              </Typography>
              <Button
                variant="contained"
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </Box>
            <Box textAlign="center" mt={4} display="flex" justifyContent="center" alignItems="center" gap={2}>
              {!isSubmitting ? (
                <Button
                  sx={{ backgroundColor: "#6586a3", marginBottom: "30px" }}
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleSubmitConfirmation}
                >
                  Submit Test
                </Button>
              ) : (
                <CircularProgress size={44} sx={{ marginBottom: "30px" }} />
              )}
            </Box>
          </>
        )}
      </Container>

      <Dialog open={showConfirmDialog} onClose={handleCancelSubmit}>
        <DialogTitle>Submit Test</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to submit your test? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubmit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Test;
